// React Query hooks for the async export pipeline.
//   useCreateExport         — useMutation to POST a new whole-contract export.
//   useRequestIssueExport   — useMutation to POST a per-issue PDF export
//                             (same pipeline, kind='issue' on the server).
//   useRequestReportExport  — useMutation to POST a Reports-tab PDF export
//                             (kind='report'); on success it also invalidates
//                             the report view it captured so the screen
//                             refetches and converges on the capture.
//   useExport               — useQuery polling every 3s while status is pending.
//   useContractExportList   — useQuery polling every 5s while any export pending.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { exportApi } from "../services/api";
import type { CreateExportInput, ContractExport, CreateExportResponse, ContractExportStatus } from "../types";
import type { CreateReportExportInput } from "../types/report";

// Terminal states — polling stops when any export reaches one of these.
// Uses ContractExportStatus (which mirrors backend/src/models/contractExport.ts
// ContractExportStatus) so a new backend status added without a matching
// frontend type update will surface as a TypeScript error.
const TERMINAL_STATUSES: ReadonlySet<ContractExportStatus> = new Set(["ready", "failed"]);

export function useCreateExport(contractId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateExportInput) =>
      exportApi.create(contractId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exports", contractId] });
    },
  });
}

// Per-issue PDF export. Posts to the dedicated endpoint, returns the
// CreateExportResponse the caller polls via `useExport`. Drawer's Download
// PDF button kicks this off, then renders a spinner against `useExport`
// until the artefact is ready.
export function useRequestIssueExport(contractId: string) {
  const queryClient = useQueryClient();

  return useMutation<CreateExportResponse, Error, string>({
    mutationFn: (itemId: string) => exportApi.requestForIssue(contractId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exports", contractId] });
    },
  });
}

// The server composes the report at request time, and an edit landing between
// the screen's last fetch and the click can appear in the capture. Refetching
// the captured view (every range and filter under it — a key prefix) keeps
// what the reader sees in step with what the PDF will say.
export function useRequestReportExport(contractId: string) {
  const queryClient = useQueryClient();

  return useMutation<CreateExportResponse, Error, CreateReportExportInput>({
    mutationFn: (input) => exportApi.requestReport(contractId, input),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["exports", contractId] });
      queryClient.invalidateQueries({ queryKey: ["report", contractId, input.view] });
    },
  });
}

export function useExport(contractId: string, exportId: string | undefined) {
  return useQuery<ContractExport>({
    queryKey: ["exports", contractId, exportId],
    queryFn: () => exportApi.get(contractId, exportId!),
    enabled: !!contractId && !!exportId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && TERMINAL_STATUSES.has(status)) return false;
      return 3_000;
    },
    staleTime: 0,
  });
}

// Recent exports for the contract — used by ExportModal to show in-modal
// status + download links so users don't have to wait for email delivery.
// Polls every 5s while ANY export in the list is not yet in a terminal state;
// stops when every export has reached ready or failed.
export function useContractExportList(contractId: string, enabled = true) {
  return useQuery<{ items: ContractExport[] }>({
    queryKey: ["exports", contractId],
    queryFn: () => exportApi.list(contractId),
    enabled: enabled && !!contractId,
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      const anyNonTerminal = items.some((e) => !TERMINAL_STATUSES.has(e.status));
      return anyNonTerminal ? 5_000 : false;
    },
    staleTime: 0,
  });
}
